<template>
  <teleport to="#navigation-container">
    <div class="navigation" :class="{ 'navigation--shrink': isShrink }">
      <div class="navigation__top">
        <TheLogoContainer />
        <div class="content">
          <NavItem
            v-for="(item, index) in sidebarItems"
            v-bind="item"
            :key="index"
            :isShrink="isShrink"
            @handle-item="handleItem(item)"
            @handle-sub-item="(subItem) => handleSubItem(item, subItem)"
            @on-mouseover="item.showSubItem = true"
            @on-mouseleave="item.showSubItem = false"
          />
        </div>
      </div>

      <div class="navigation__bottom">
        <BaseButton
          :text="!isShrink ? $t('actions.logout') : ''"
          size="small"
          :leftIcon="Logout"
          fluid
          @on-click="handleLogout"
        />
      </div>
      <span
        class="navigation__collapsable"
        :class="{ rotate: !isShrink }"
        @click="handleShrink"
      >
        <BaseSvg :src="ChevronRight" alt="right-arrow" />
      </span>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import NavItem, { type INavbarItem } from '@/components/Base/NavItem.vue';
import { ChevronRight, Logout } from '@/assets/icons';
import { useRouter, useRoute } from 'vue-router';
import { useSidebarStore, useUserStore } from '@/stores';
import { storeToRefs } from 'pinia';
import { navigationData } from '@/resources/navigation';
import { watch } from 'vue';
import { BaseSvg, BaseButton } from '@/components/Base';
import TheLogoContainer from '@/components/TheLogoContainer.vue';

const sidebarItems = ref(navigationData);
const store = useSidebarStore();
const userStore = useUserStore();
const { isShrink } = storeToRefs(store);

const route = useRoute();
const router = useRouter();

watch(
  () => route.path,
  (newVal) => {
    const path = newVal;
    sidebarItems.value.forEach((item) => {
      if (item.link) {
        item.selected = path.startsWith(item.link);
      }
      if (item.subItems) {
        item.subItems.forEach((subItem) => {
          subItem.selected = path === `${item.link}${subItem.link}`;
        });
      }
    });
    store.handleAppClassName();
  },
  { deep: true, immediate: true }
);

const handleItem = (item: any): void => {
  if (item.disabled) {
    return;
  }
  if (item.subItems?.length) {
    if (!item.showSubItem) {
      sidebarItems.value.forEach((item) => (item.showSubItem = false));
    }
    item.showSubItem = !item.showSubItem;
  } else if (item.link) {
    router.push(item.link);

    (sidebarItems.value || []).forEach((el) => {
      if (el.subItems) {
        el.subItems.forEach((el) => (el.selected = false));
      }
      el.selected = false;
    });
    item.selected = true;
  }
};

const handleSubItem = (item: INavbarItem, subItem: INavbarItem): void => {
  if (subItem.disabled || item.disabled) {
    return;
  }
  if (subItem.link) {
    router.push(`${item.link}${subItem.link}`);
    (sidebarItems.value || []).forEach((el) => {
      el.selected = false;
      if (isShrink.value) {
        el.showSubItem = false;
      }
      if (el.subItems?.length) {
        (el.subItems || [])?.forEach((el) => {
          el.selected = false;
        });
      }
    });
    subItem.selected = true;
  }
};
const handleShrink = () => {
  let isAnySubItemVisible: boolean | undefined = false;
  sidebarItems.value?.forEach((item) => {
    isAnySubItemVisible = !isAnySubItemVisible
      ? item.showSubItem
      : isAnySubItemVisible;
    item.showSubItem = false;
  });
  const shrinkTimeout = !isShrink.value && isAnySubItemVisible ? 370 : 0;
  setTimeout(() => {
    store.handleShrink();
  }, shrinkTimeout);
};
const handleLogout = () => {
  userStore.logout();
};
</script>

<style scoped lang="scss">
.navigation {
  background-color: $white;
  height: 100vh;
  width: 218px;
  padding: 12px 8px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  border-right: 1px solid $stroke;
  box-sizing: border-box;
  position: absolute;
  @include transition(all);
  font-size: 16px;
  &--shrink {
    width: 72px;
    align-items: flex-start;
  }
  &:not(&--shrink) {
    & .navigation__top {
      overflow: auto;
      & .content {
        overflow: auto;
      }
    }
  }
  &__top {
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
    align-items: flex-start;
    & .smart-logo {
      margin-bottom: 8px;
      align-self: center;
      & :deep(svg) {
        @include transition(all);
        transform-origin: left;
        width: 50px;
      }
    }
    & .content {
      display: flex;
      flex-direction: column;
      gap: 12px;
      width: 100%;
      align-items: flex-start;
    }
  }
  &__bottom {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    gap: 24px;
  }
  &__collapsable {
    z-index: 3;
    position: absolute;
    width: 32px;
    height: 32px;
    border-radius: 100%;
    background: $disabled-light-200;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    right: -16px;
    bottom: 120px;
    border: 1px solid $stroke;
    @include transition(all);
    &:hover {
      background: $disabled;
    }
    &.rotate {
      rotate: 180deg;
    }
    & img {
      width: 12px;
      height: 12px;
    }
  }
}
</style>
